import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {DatePipe} from '@angular/common';
import {RouterLink} from '@angular/router';
import {Contract} from '@shared/api/models/query/contract.model';
import {
    isOrganisationActive,
    OrganisationDetail,
    OrganisationLinkedContract
} from '@shared/api/models/query/organisation.model';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {ContractsService} from '@shared/api/services/v2/contracts.service';
import {Pagination} from '@models/pagination.model';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {contractDetailRoute} from '@shared/services/router/contract-routes';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {LinkedRecordsTabBase} from '@shared/components/linked-records/linked-records-tab.base';
import {LinkRecordsModalConfig} from '@shared/components/linked-records/link-records.modal';

/** Safety bound on the pages read for the link popup (the contracts list has no `linked=false`). */
const MAX_CANDIDATE_PAGES = 100;

/**
 * UNIVAPLAT-3695: Contracts tab of the organisation detail page.
 *
 * - Lists the contracts linked to the organisation. Read-only for organisation admins; link and
 *   unlink need MANAGE_ORGANISATION_CONTRACTS (general admins) and an active organisation.
 * - The API answers 422 on the linked lists of a deactivated or deleted organisation, so for those
 *   the tab shows a message and does not load.
 * - The backend has no `linked=false` for this list: the link popup pages through every page of
 *   `ContractsService.list` (active contracts, default page size) and drops the ids already linked.
 * - Titles link to the contract details only for holders of VIEW_CONTRACTS.
 * - `changed` (from the base) lets the page refresh `linkedContracts` for the delete guard.
 * - Backend owns the error text. Contract data is never logged and is dropped on destroy.
 */
@Component({
    selector: 'cdss-organisation-contracts-tab',
    standalone: true,
    imports: [DatePipe, RouterLink, ConfirmDialogDirective, SpinnerComponent],
    templateUrl: './organisation-contracts-tab.component.html',
    styleUrl: './organisation-contracts-tab.component.scss'
})
export class OrganisationContractsTabComponent extends LinkedRecordsTabBase<OrganisationLinkedContract> implements OnChanges {
    @Input() organisation!: OrganisationDetail;

    protected readonly entityLabel = 'Contract';

    private readonly organisationsService = inject(OrganisationsService);
    private readonly contractsService = inject(ContractsService);
    private readonly authService = inject(AuthService);

    get isActive(): boolean {
        return !!this.organisation && isOrganisationActive(this.organisation);
    }

    get canWrite(): boolean {
        return this.isActive && this.hasPermission(Permissions.MANAGE_ORGANISATION_CONTRACTS);
    }

    get canViewContract(): boolean {
        return this.hasPermission(Permissions.VIEW_CONTRACTS);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['organisation']) {
            return;
        }
        const previous = changes['organisation'].previousValue as OrganisationDetail | undefined;
        const current = this.organisation;
        // Reload on first render, a different organisation, or an active/inactive flip.
        if (!previous || previous.id !== current?.id || isOrganisationActive(previous) !== isOrganisationActive(current)) {
            void this.load();
        }
    }

    rowId(row: OrganisationLinkedContract): number {
        return row.id;
    }

    contractLink(row: OrganisationLinkedContract): string {
        return '/' + contractDetailRoute(row.id);
    }

    /** Text marker (never colour alone) for a deactivated or deleted contract. */
    contractStatus(row: OrganisationLinkedContract): string | null {
        if (row.deletedAt) {
            return 'Deleted';
        }
        return row.disabledAt ? 'Deactivated' : null;
    }

    protected async fetch(): Promise<OrganisationLinkedContract[]> {
        if (!this.organisation || !this.isActive) {
            return [];
        }
        return this.organisationsService.listContracts(this.organisation.id);
    }

    protected async unlinkOne(row: OrganisationLinkedContract): Promise<void> {
        await this.organisationsService.removeContract(this.organisation.id, row.id);
    }

    protected linkModalConfig(): LinkRecordsModalConfig<Contract> {
        const organisationId = this.organisation.id;
        const linkedIds = new Set(this.rows.map(row => row.id));
        return {
            heading: 'Link contracts',
            titleId: 'link-organisation-contracts-title',
            columns: [
                {header: 'Title', value: contract => contract.title},
                {header: 'Code', value: contract => contract.code},
                {header: 'Service provider', value: contract => contract.serviceProvider},
            ],
            loader: async () => (await this.listAllContracts()).filter(contract => !linkedIds.has(contract.id)),
            submitLabel: 'Add selected to organisation',
            successMessage: 'Contracts linked to organisation',
            emptyMessage: 'No contracts left to link',
            submit: async (ids: number[]) => {
                await this.organisationsService.addContracts(organisationId, ids);
            },
        };
    }

    /** Reads every page of the active contracts with the default page size. */
    private async listAllContracts(): Promise<Contract[]> {
        const all: Contract[] = [];
        for (let page = 0; page < MAX_CANDIDATE_PAGES; page++) {
            const pagination = new Pagination();
            pagination.start = page;
            const response = await this.contractsService.list(pagination);
            const data = response?.data ?? [];
            all.push(...data);
            const totalPages = response?.pagination?.totalPages;
            const lastPage = totalPages !== undefined && totalPages !== null
                ? page + 1 >= totalPages
                : data.length < pagination.size;
            if (data.length === 0 || lastPage) {
                break;
            }
        }
        return all;
    }

    private hasPermission(permission: Permissions): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[permission] ?? []).some(role => userRoles.includes(role));
    }
}
